<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import copyToClipBoard from '@utils/copy-to-clipboard'
import { siteUrl } from '@/config/site'
import { shareOnFacebook, shareOnX } from '@utils/social-share'
import { useAppearanceStore } from '@state/stores/appearance'
import { useAuthStore } from '@state/stores/auth'
import { useFavoritesStore } from '@state/stores/favorites'
import { useMessages } from '@composables/use-messages'

const props = defineProps({
    channel: {
        type: Object,
        default: () => {},
    },
    // Defaults are the /radio channel-tile menu (channel-detail-tile.vue), which passes
    // neither: it keeps View Channel and gets no settings links. The channel-page hero
    // inverts both — the same way the artist hero passes `hasViewArtistOption: false`.
    hasViewChannelOption: {
        type: Boolean,
        default: true,
    },
    hasSettingsLinks: {
        type: Boolean,
        default: false,
    },
})

const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const favoritesStore = useFavoritesStore()
const router = useRouter()
const getMessageText = useMessages()

const url = computed(() => {
    // Router 4 throws on a falsy required param, and this resolves during render.
    if (!props.channel.id) return ''

    const href = router.resolve({ name: 'channel', params: { channelId: props.channel.id } }).href
    return siteUrl(href)
})

const isFavourite = computed(() => favoritesStore.channelIds.includes(props.channel.id))

const header = computed(() => ({ title: props.channel.title, subtitle: 'Radio channel' }))

function copyLinkToClipBoard() {
    copyToClipBoard(url.value)
    appearanceStore.handleNotification({
        message: getMessageText('LINK_COPIED_CLIPBOARD'),
        type: 'success',
    })
}

// The same gate _base-favourites.vue applied to this row on master: signed in → toggle
// the channel (no notification); signed out → login.
function toggleFavourite() {
    if (authStore.loggedIn) favoritesStore.toggleChannel(props.channel.id)
    else router.push({ name: 'login' })
}

// A row whose channel has no id is hidden: View Channel's router-link would throw resolving
// a missing param, and master's favourite button went inert (pointer-events: none) for it.
const items = computed(() => [
    {
        id: 'view',
        label: 'View Channel',
        to: { name: 'channel', params: { channelId: props.channel.id } },
        hidden: !props.hasViewChannelOption || !props.channel.id,
    },
    // The settings cluster and the divider that closes it appear together or not at all
    // (master's single v-if over the group) — so no divider sits between View Channel and
    // the favourite row on the /radio tiles.
    ...(props.hasSettingsLinks
        ? [
            { id: 'audio-quality', label: 'Audio Quality', to: { name: 'settings/audio-quality' } },
            { id: 'external-player', label: 'Play in External Player', to: { name: 'settings/external-player' } },
            { divider: true },
        ]
        : []),
    {
        id: 'favourite',
        label: isFavourite.value ? `Remove ${props.channel.title} from Favorites` : `Add ${props.channel.title} to Favorites`,
        hidden: !props.channel.id,
        action: toggleFavourite,
    },
    { divider: true },
    // Interim (R8): today's three share actions as plain rows. The single `Share ›` row
    // lands with the Share surface ticket 1218209213218817.
    { id: 'share-facebook', label: 'Facebook', action: () => shareOnFacebook(url.value) },
    { id: 'share-x', label: 'X', action: () => shareOnX(url.value, `${props.channel.title} on @friskyradio #feelinfrisky`) },
    { id: 'copy-link', label: 'Copy Link', action: copyLinkToClipBoard },
])
</script>

<template>
    <BaseContextMenu
        class="channel-options"
        :items="items"
        :header="header"
        trigger-label="Channel options"
    >
        <!-- The station's colour dot as a 44 square; an unknown id leaves the frame's
             placeholder fill showing. -->
        <template #header-art>
            <span :class="['channel-options-swatch', channel.id && `channel-options-swatch-${channel.id}`]" />
        </template>
        <template
            v-if="$slots.trigger"
            #trigger="slotProps"
        >
            <slot
                name="trigger"
                v-bind="slotProps"
            />
        </template>
    </BaseContextMenu>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.channel-options-swatch {
    display: block;

    @include square(100%);
}

.channel-options-swatch-frisky {
    background: $c-frisky-fill;
}

.channel-options-swatch-chill {
    background: $c-chill-fill;
}

.channel-options-swatch-classics {
    background: $c-classics-fill;
}

.channel-options-swatch-deep {
    background: $c-deep-fill;
}
</style>
